<template>
  <div data-testid="tpg-prompt-viewer">
    <!-- 🔴 An answer read WITHOUT the extractor is not the same as "nothing happened", and this tab is the
         only place a reader would look. Without this note a deterministic turn shows an empty panel (or just
         the phraseQuestion call) and reads as a runtime that skipped its own job. -->
    <v-alert
      v-if="noExtractNote"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-2"
      data-testid="tpg-llm-no-extract"
    >
      <div>{{ noExtractNote }}</div>
      <div class="text-caption mt-1">ขั้นตอนที่เกิดขึ้นจริงดูได้ที่แท็บ TRACE</div>
    </v-alert>

    <div
      v-if="!calls.length"
      class="text-medium-emphasis text-caption pa-2"
      data-testid="tpg-llm-empty"
    >เทิร์นนี้ไม่มีการเรียก LLM</div>

    <v-expansion-panels v-else v-model="openCalls" multiple>
      <v-expansion-panel
        v-for="(c, i) in calls"
        :key="i"
        :data-testid="'tpg-llm-call-' + i"
      >
        <v-expansion-panel-title>
          <div class="d-flex align-center flex-wrap ga-1">
            <span class="font-weight-medium">{{ opTitle(c) }}</span>
            <span class="text-caption text-medium-emphasis">({{ c.op || '—' }})</span>
            <v-chip v-if="c.model" size="x-small" variant="tonal" :title="'โมเดล ' + c.model">{{ c.model }}</v-chip>
            <v-chip size="x-small" variant="outlined" title="จำนวน token ทั้งหมดของ call นี้">{{ c.totalTokens ?? 0 }} tk</v-chip>
            <v-chip size="x-small" variant="outlined" title="เวลาที่ใช้">{{ seconds(c) }}s</v-chip>
            <v-chip
              v-if="isFailed(c)"
              size="x-small"
              color="orange"
              variant="tonal"
              prepend-icon="mdi-alert"
              title="เรียก LLM ไม่สำเร็จ (node fail-soft)"
              :data-testid="'tpg-llm-failed-' + i"
            >LLM ล้ม (fail-soft)</v-chip>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <!-- §UX ruling D — ONLY on the extractFields card (the 80-ref cap lives there). -->
          <v-alert
            v-if="c.op === 'extractFields' && showAskableWarning"
            type="warning"
            variant="tonal"
            density="compact"
            icon="mdi-alert-outline"
            class="mb-2"
            data-testid="tpg-askable-warning"
          >
            ชุดคำถามนี้มีข้อที่ถามได้ {{ askableCount }} ข้อ แต่ prompt ส่งรายการ ref ให้ LLM ได้สูงสุด 80 ข้อ — ข้อที่เกินจะถูกตัดออก จึงอาจสกัดคำตอบลง field ท้าย ๆ ไม่ได้
            <div class="text-caption text-medium-emphasis mt-1">
              (ข้อจำกัดของ node — ปลดเพดานเป็นงานเฟสถัดไป ดู §NEXT)
            </div>
          </v-alert>

          <v-alert
            v-if="isFailed(c)"
            type="error"
            density="compact"
            variant="tonal"
            class="mb-2"
            :data-testid="'tpg-llm-error-' + i"
          >เรียก LLM ไม่สำเร็จ (statusReturn={{ c.statusReturn ?? 0 }}) — {{ c.error || '—' }}</v-alert>

          <!-- composition legend -->
          <div
            v-if="layersOf(c).length"
            class="cr-legend"
            :data-testid="'tpg-llm-legend-' + i"
          >
            <span class="cr-legend-label">ประกอบจาก {{ layersOf(c).length }} step:</span>
            <span
              v-for="(l, j) in layersOf(c)"
              :key="'lg-' + j"
              class="cr-chip"
              :style="{ borderColor: layerColor(l), color: layerColor(l) }"
              :title="l.sourceLabelTh || l.source || ''"
            >{{ l.marker || '—' }}</span>
          </div>

          <!-- per-layer provenance -->
          <div
            v-for="(l, j) in layersOf(c)"
            :key="'ly-' + j"
            class="cr-section"
            :style="{ borderLeftColor: layerColor(l) }"
            :data-testid="'tpg-layer-' + i + '-' + j"
          >
            <div class="cr-header-row">
              <div class="cr-header">{{ l.marker || '—' }}</div>
              <span class="cr-role" :style="{ background: layerColor(l) }">{{ l.sourceLabelTh || l.source || 'ที่มาไม่ระบุ' }}</span>
            </div>

            <div v-if="allowEdit && hasEditLink(l)" class="cr-actions">
              <v-btn
                size="x-small"
                variant="outlined"
                prepend-icon="mdi-pencil-outline"
                :data-testid="'tpg-layer-edit-' + i + '-' + j"
                @click="$emit('open-edit', l.editRef)"
              >{{ editButtonLabel(l) }}</v-btn>
              <v-btn
                v-if="askedFieldKey && isQuestionSetLayer(l)"
                size="x-small"
                variant="text"
                prepend-icon="mdi-content-copy"
                :data-testid="'tpg-layer-copyfieldkey-' + i + '-' + j"
                @click="onCopy(askedFieldKey, 'FieldKey')"
              >คัดลอก FieldKey</v-btn>
              <div class="cr-caption">{{ editCaption(l) }}</div>
            </div>
            <!-- 🔴 Edit switched off by the parent (`allow-edit=false`, e.g. the Tax Log viewer
                 reading an export FILE) while the layer DOES have a config row behind it. It must
                 not fall through to `noEditBadge` below — that would call a settings-table
                 sentence "ค่า default ในโค้ด". Say where it lives instead, in words. -->
            <div v-else-if="hasEditLink(l)" class="cr-actions">
              <v-chip
                size="x-small"
                color="grey"
                variant="tonal"
                :data-testid="'tpg-layer-editoff-' + i + '-' + j"
              >มาจากคอนฟิก — เปิดแก้จากหน้านี้ไม่ได้</v-chip>
              <div class="cr-caption">{{ editCaption(l) }}</div>
            </div>
            <div v-else class="cr-actions">
              <v-chip
                size="x-small"
                color="grey"
                variant="tonal"
                :title="l.sourceLabelTh || l.source || noEditBadge(l)"
                :data-testid="'tpg-layer-nodedefault-' + i + '-' + j"
              >{{ noEditBadge(l) }}</v-chip>
            </div>

            <div class="d-flex justify-end">
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                title="คัดลอกชั้น prompt นี้"
                aria-label="คัดลอกชั้น prompt นี้"
                :data-testid="'tpg-layer-copy-' + i + '-' + j"
                @click="onCopy(l.text || '', 'Prompt layer — ' + (l.marker || ''))"
              />
            </div>
            <pre class="code-block code-block--prompt">{{ l.text || '(ว่าง)' }}</pre>
          </div>

          <!-- full system prompt -->
          <div class="cr-block">
            <div class="cr-block-head">
              <span class="text-caption font-weight-medium">System prompt (เต็มก้อนที่ส่งเข้า LLM)</span>
              <v-spacer />
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                title="คัดลอก system prompt"
                aria-label="คัดลอก system prompt"
                :data-testid="'tpg-llm-systemprompt-copy-' + i"
                @click="onCopy(c.systemPrompt || '', 'System prompt')"
              />
            </div>
            <pre
              class="code-block code-block--prompt"
              :data-testid="'tpg-llm-systemprompt-' + i"
            >{{ c.systemPrompt || '(ว่าง)' }}</pre>
          </div>

          <!-- user text -->
          <div class="cr-block">
            <div class="cr-block-head">
              <span class="text-caption font-weight-medium">ข้อความผู้ใช้ที่ส่งเข้า LLM (userText)</span>
              <v-spacer />
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                title="คัดลอก userText"
                aria-label="คัดลอก userText"
                :data-testid="'tpg-llm-usertext-copy-' + i"
                @click="onCopy(c.userText || '', 'userText')"
              />
            </div>
            <pre class="code-block" :data-testid="'tpg-llm-usertext-' + i">{{ c.userText || '(ว่าง)' }}</pre>
            <div
              v-if="isByDesignEmptyUserText(c)"
              class="cr-caption"
              :data-testid="'tpg-llm-usertext-bydesign-' + i"
            >{{ EMPTY_USERTEXT_NOTE }}</div>
          </div>

          <!-- raw response -->
          <div class="cr-block">
            <div class="cr-block-head">
              <span class="text-caption font-weight-medium">คำตอบดิบจาก LLM (rawResponse)</span>
              <v-spacer />
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                title="คัดลอก rawResponse"
                aria-label="คัดลอก rawResponse"
                :data-testid="'tpg-llm-rawresponse-copy-' + i"
                @click="onCopy(c.rawResponse || '', 'rawResponse')"
              />
            </div>
            <pre class="code-block" :data-testid="'tpg-llm-rawresponse-' + i">{{ prettyRaw(c) }}</pre>
          </div>

          <!-- raw request — only an export file carries it (`Log_Monitor.ContentRequest`
               verbatim); live payloads leave it null, so the block simply never renders there. -->
          <div v-if="hasRawRequest(c)" class="cr-block" :data-testid="'tpg-llm-rawrequest-block-' + i">
            <div class="cr-block-head">
              <span class="text-caption font-weight-medium">Request body ที่ส่งจริง</span>
              <v-spacer />
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                title="คัดลอก request body"
                aria-label="คัดลอก request body"
                :data-testid="'tpg-llm-rawrequest-copy-' + i"
                @click="onCopy(c.rawRequest || '', 'Request body')"
              />
            </div>
            <div class="cr-caption mb-1">จากไฟล์ export (Log_Monitor.ContentRequest)</div>
            <pre class="code-block" :data-testid="'tpg-llm-rawrequest-' + i">{{ prettyRequest(c) }}</pre>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
// Prompt & LLM-call viewer of the Tax Playground V2 console (S3-P4 · task 4-A).
//
// Ported from the POC `ContentRequestView.vue` — but ONLY its layout, class names
// and reading order. Two things were deliberately NOT ported:
//
// 1. 🔴 The POC's `MARKER_RE` / `parse()` / 17-row `ROLE` map keyed on
//    `### Marker ###` strings. `promptLayers` arrives ALREADY split from the
//    backend, so nothing is re-parsed here. (Since the prompt-stack migration
//    `marker` IS the literal marker line of the prompt — `### Tax Node Prompt ###`
//    etc., or `(base system prompt)` for the unmarked base layer — but it is still
//    only DISPLAYED, never matched.) Layer colour is keyed on `layer.source`.
//    🔴 The same is true of a PRODUCTION turn in the Tax Log viewer: the backend
//    splits the stored prompt there too (`TaxPromptStackV2.SplitStack`), but those
//    layers carry text only — `source`/`editRef` are null because production
//    persists no provenance. Everything below must therefore keep degrading on a
//    missing `source` instead of assuming one.
// 2. The POC's dark `v-sheet` wrapper — here each block owns its own `.code-block`
//    so the panel chrome stays light and only machine text is dark (§Accessibility A5).
//
// 🔴 `editRef` has NO `fieldKey` (`TaxPlaygroundPromptEditRefDto` carries kind /
// configVersionId / questionVersionId / stepId / groupCode / questionItemId /
// groupLabel / itemLabel). The "คัดลอก FieldKey" button therefore uses the
// `askedFieldKey` prop and HIDES ITSELF when the turn asked nothing (completed form)
// or when the layer is not a question-set layer (a global settings prompt has no field).

/**
 * Layer colour keyed on `source` (NOT on the marker text) — exact match first, then
 * the source FAMILY prefix, then a neutral fallback, so a new `config:<Entity>.<Col>`
 * never loses its section border.
 */
const LAYER_COLOR = {
  'config:TaxQuestionStepV2.QuestionSystemPrompt': '#5c6bc0',
  'config:TaxQuestionItemV2.AnswerSystemPrompt': '#26a69a',
  nodeDefault: '#78909c',
};
/** Prefix → colour, checked in order (longest/most specific first). */
const LAYER_COLOR_BY_PREFIX = [
  ['config:TaxPlanningSettingsV2.', '#8e6bc0'],
  ['config:TaxQuestionStepV2.', '#5c6bc0'],
  ['config:TaxQuestionItemV2.', '#26a69a'],
  ['node:', '#ef8a3c'],
];
const LAYER_COLOR_FALLBACK = '#9aa4b2';

/** Badge for a layer with no `editRef` — a dead button is worse than none. Keyed on the source family. */
const NODE_DEFAULT_BADGE = 'ค่า default ในโค้ด (ไม่มีช่องคอนฟิกให้แก้)';
const NODE_DATA_BADGE = 'แก้ที่โหนดใน Flow canvas';

/**
 * 🔴 A layer with NO `source` is not a `nodeDefault` layer — it is a layer whose provenance was never
 * recorded. Production chat (`log.Log_Monitor`) stores only the prompt text, so the backend can split it
 * back into sections but cannot say which config row wrote each one. Showing the nodeDefault badge here
 * would state "ค่า default ในโค้ด" about a sentence that may well have come from the settings table.
 */
const NO_PROVENANCE_BADGE = 'ไม่ได้บันทึกที่มาของชั้นนี้ (แชทจริง)';

/**
 * Since 2026-10-05 `phraseQuestion` sends a FIXED TASK LINE as its `userText` (backend `TaxPhraseTaskLineV2`):
 * `Write Genie's next message in {Language} only.` — plus ` End with the question.` when the copy is a question.
 * It is never the question and never the customer's words: the question still rides in `### Question Context ###`
 * and what the customer just said in the last `USER:` line of `### History ###`.
 * An EMPTY `userText` on this op therefore means a turn persisted BEFORE that date (`LlmCallsJson` of an old
 * playground turn), when the op sent no user turn on purpose. Kept so those turns still read correctly — empty
 * here is history, not an anomaly, and must never be dressed up as one.
 */
const EMPTY_USERTEXT_NOTE =
  'ตั้งแต่ 2026-10-05 op นี้ส่งบรรทัดงานคงที่ (`Write Genie\'s next message in … only.`) ใน userText — ' +
  'ช่องว่างแปลว่าเทิร์นนี้ถูกบันทึกก่อนหน้านั้น (ตอนนั้น op นี้ไม่ส่งข้อความผู้ใช้โดยตั้งใจ: ' +
  'คำถามอยู่ในชั้น ### Question Context ### และข้อความล่าสุดของลูกค้าอยู่บรรทัดสุดท้ายของชั้น ### History ###) · ' +
  'ไม่ใช่ความผิดปกติ';

/** Pretty-printing a 200 KB blob freezes the tab; above this it is shown raw. */
const RAW_PRETTY_LIMIT = 200 * 1024;

export default {
  name: 'TaxPlaygroundPromptViewerV2',
  props: {
    /** `TaxPlaygroundLlmCallDto[]` — length varies: first turn 1 · normal 2 · re-ask 1 · form-complete 1 · year-select 0. */
    llmCalls: { type: Array, default: () => [] },
    /** Σ configForm.groups[].fields.length — computed by the parent (the ONLY number the FE may compute). */
    askableCount: { type: Number, default: 0 },
    /** `activeTurn.asked.fieldKey`; null on a completed form ⇒ the copy button hides. */
    askedFieldKey: { type: String, default: null },
    /**
     * The customer's message this turn, or null. Used ONLY to tell "the runtime answered without the
     * extractor" from "there was nothing to answer" — a turn with no message (the opening greeting, a
     * re-render) legitimately has no extract call and deserves no note.
     */
    userMessage: { type: String, default: null },
    /**
     * false ⇒ the "แก้คอนฟิก…" buttons never render, even on a layer with an `editRef` (the
     * layer then says where it lives, in words). The Tax Log viewer turns it off when it reads
     * an export FILE: those refs point at config rows of the environment the file came from.
     * Default true — the playground passes nothing and keeps its buttons.
     */
    allowEdit: { type: Boolean, default: true },
  },
  emits: ['open-edit', 'copy'],
  data() {
    return {
      openCalls: [0],
      NODE_DEFAULT_BADGE,
      NODE_DATA_BADGE,
      NO_PROVENANCE_BADGE,
      EMPTY_USERTEXT_NOTE,
    };
  },
  computed: {
    calls() {
      return this.llmCalls || [];
    },
    /**
     * Thai note for a turn where the customer said something but `extractFields` never ran — null otherwise.
     *
     * The runtime answers deterministically whenever it can: a reply that matches a parked clarify's own
     * options ("ต่อปี" against MONTH/YEAR) is patched straight into the line-item, and a tax-year pick is
     * matched against the year list. Both are cheaper AND safer than asking a model to re-read a choice it
     * already offered — but both leave this tab looking like the turn did nothing.
     *
     * 🔴 Keyed off the ABSENCE of the extract op, not off a guess at which path ran. The wording therefore
     * names what is certain (no extractor) and where to look for the rest, rather than asserting a reason
     * that would be wrong for the other path.
     */
    noExtractNote() {
      if (!String(this.userMessage || '').trim()) return null;
      const ran = this.calls.some((c) => String((c && c.op) || '').toLowerCase() === 'extractfields');
      if (ran) return null;
      return 'เทิร์นนี้ไม่ได้เรียก LLM ขั้นสกัดคำตอบ — คำตอบตรงกับตัวเลือกที่ระบบถามไว้อยู่แล้ว '
           + '(เช่น ต่อเดือน/ต่อปี ของ clarify หรือการเลือกปีภาษี) จึงบันทึกได้เลยโดยไม่ต้องให้โมเดลตีความ';
    },
    showAskableWarning() {
      return (this.askableCount ?? 0) > 80;
    },
  },
  methods: {
    /** B8 / TC-67 (ก) — the Thai label always leads; the English op follows in small type. */
    opTitle(c) {
      return (c && (c.opLabelTh || c.op)) || '—';
    },
    /** 🔴 L6 — a failed call is detected HERE, never from the turn-level `status`. */
    isFailed(c) {
      return (c && ((c.statusReturn ?? 0) !== 0 || Boolean(c.error))) || false;
    },
    seconds(c) {
      return (((c && c.durationMs) ?? 0) / 1000).toFixed(1);
    },
    layersOf(c) {
      return (c && c.promptLayers) || [];
    },
    /**
     * A layer is actionable whenever the backend handed it an `editRef` — NOT when its
     * source starts with `config:`. A `nodeDefault` layer that a SETTINGS column can
     * override carries a ref too ("not overridden yet" ≠ "not editable").
     */
    hasEditLink(l) {
      return Boolean(l && l.editRef);
    },
    /** Only a question-set layer has a FieldKey behind it; a global settings prompt does not. */
    isQuestionSetLayer(l) {
      const kind = (l && l.editRef && l.editRef.kind) || '';
      return kind === 'questionStep' || kind === 'questionItem';
    },
    editButtonLabel(l) {
      const kind = (l && l.editRef && l.editRef.kind) || '';
      return kind === 'configVersion' ? 'แก้ที่ตั้งค่าส่วนกลาง' : 'แก้คอนฟิกข้อนี้';
    },
    /** Badge for a layer with no edit link — says WHERE it lives instead of "แก้ไม่ได้" when it is editable elsewhere. */
    noEditBadge(l) {
      const source = String((l && l.source) || '');
      if (!source) return NO_PROVENANCE_BADGE;
      return source.startsWith('node:') ? NODE_DATA_BADGE : NODE_DEFAULT_BADGE;
    },
    layerColor(l) {
      const source = String((l && l.source) || '');
      if (LAYER_COLOR[source]) return LAYER_COLOR[source];
      const hit = LAYER_COLOR_BY_PREFIX.find(([prefix]) => source.startsWith(prefix));
      return (hit && hit[1]) || LAYER_COLOR_FALLBACK;
    },
    /** The edit page cannot anchor yet (§UX ruling B) — so the caption says where to look, in words. */
    editCaption(l) {
      const ref = (l && l.editRef) || {};
      if (ref.kind === 'configVersion') {
        return `เปิดหน้า Tax Config V2 → ปุ่ม SETTINGS → ${ref.itemLabel || 'ตั้งค่าพรอมต์ส่วนกลาง'}`;
      }
      const group = ref.groupLabel || ref.groupCode || '—';
      const item = ref.itemLabel || '—';
      return ref.kind === 'questionStep'
        ? `เปิดหน้าชุดคำถาม → หมวด “${group}” → ตั้งค่าขั้นสูงของหมวด`
        : `เปิดหน้าชุดคำถาม → หมวด “${group}” → ข้อ “${item}”`;
    },
    /** Empty `userText` on `phraseQuestion` = a turn persisted BEFORE 2026-10-05 (the op now sends a fixed task line) — still not an anomaly, see EMPTY_USERTEXT_NOTE. */
    isByDesignEmptyUserText(c) {
      return Boolean(c && c.op === 'phraseQuestion' && !String(c.userText || '').trim());
    },
    prettyRaw(c) {
      return this.prettyJson((c && c.rawResponse) || '');
    },
    /**
     * Export files only — a non-blank string, else the block stays hidden.
     * `/\S/` rather than `.trim()`: a request body can be MBs, and trim copies it on every render.
     */
    hasRawRequest(c) {
      return Boolean(c && typeof c.rawRequest === 'string' && /\S/.test(c.rawRequest));
    },
    prettyRequest(c) {
      return this.prettyJson((c && c.rawRequest) || '');
    },
    /** Pretty-print when it is JSON and small enough (RAW_PRETTY_LIMIT); otherwise verbatim. */
    prettyJson(raw) {
      if (!raw) return '(ว่าง)';
      if (raw.length > RAW_PRETTY_LIMIT) return raw;
      try {
        return JSON.stringify(JSON.parse(raw), null, 2);
      } catch (e) {
        return raw;
      }
    },
    onCopy(text, label) {
      this.$emit('copy', { text, label });
    },
  },
};
</script>

<style scoped>
.cr-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  padding: 4px 4px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 10px;
}

.cr-legend-label {
  font-size: 11px;
  color: #78909c;
  margin-right: 2px;
}

.cr-chip {
  font-family: monospace;
  font-size: 10px;
  border: 1px solid;
  border-radius: 10px;
  padding: 1px 7px;
  white-space: nowrap;
}

.cr-section {
  margin-bottom: 10px;
  border-left: 3px solid #9aa4b2;
  padding-left: 8px;
}

.cr-header-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.cr-header {
  font-family: monospace;
  font-weight: 600;
  font-size: 12px;
  color: #546e7a;
  padding: 2px 0;
}

.cr-role {
  font-size: 10px;
  font-weight: 600;
  color: #0b0d10;
  padding: 1px 7px;
  border-radius: 10px;
}

.cr-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0;
}

.cr-caption {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  flex-basis: 100%;
}

.cr-block {
  margin-top: 10px;
}

.cr-block-head {
  display: flex;
  align-items: center;
}

/* 🔴 `overflow-wrap: anywhere`, NOT `word-break: break-word/break-all`. Prompts
   are read, not skimmed: a break is allowed only when a token genuinely does not
   fit, so marker lines (`### Tax Question Prompt ###`) and field keys survive
   intact, while Thai — which offers the browser no break opportunity at all —
   still wraps. Whatever is left over scrolls INSIDE this box, so a long line can
   never widen the inspector column. */
.code-block {
  font-family: monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: normal;
  overflow-wrap: anywhere;
  background: #0f1115;
  color: #dbe2ea;
  border-radius: 6px;
  padding: 8px;
  max-height: 34vh;
  overflow: auto;
  overflow-x: auto;
}

.code-block--prompt {
  max-height: 40vh;
}
</style>
